import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  DEFAULT_TIME_SERIES_DUPLICATE_HANDLING,
  TIME_SERIES_DUPLICATE_HANDLING,
  TimeSeriesDuplicateHandling,
} from '@anessa/shared-models';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';
import { ErrorService } from '../../shared/services/error/error.service';
import { ProjectService } from '../../shared/services/project/project.service';
import { TimeSeriesTablesService } from '../../features/timeseries-data/timeseries-tables.service';
import { TimeseriesSheetImporterService } from './services/timeseries-sheet-importer.service';
import { GetMatchedSheetsResult, MultiTabImportService } from './multi-tab-import.service';
import { ImportResult, SheetParseResult } from './schemas/sheet-importer.interface';

@Component({
  selector: 'app-multi-tab-import-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    I18nPipe,
    I18nScopeReadyDirective,
  ],
  providers: [
    provideI18nScope('ui/multi-tab-import-dialog'),
    I18nScopedService,
  ],
  templateUrl: './multi-tab-import-dialog.html',
  styleUrl: './multi-tab-import-dialog.scss',
})
export class MultiTabImportDialog implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<MultiTabImportDialog>);
  private readonly projectService = inject(ProjectService);
  private readonly timeseriesTablesService = inject(TimeSeriesTablesService);
  private readonly multiTabImportService = inject(MultiTabImportService);
  private readonly tsSheetImporter = inject(TimeseriesSheetImporterService);
  private readonly errorService = inject(ErrorService);
  readonly i18n = inject(I18nScopedService);

  readonly duplicateHandlingOptions = TIME_SERIES_DUPLICATE_HANDLING;
  duplicateHandling: TimeSeriesDuplicateHandling = DEFAULT_TIME_SERIES_DUPLICATE_HANDLING;

  selectedFile: File | null = null;
  matchResult: GetMatchedSheetsResult | null = null;
  parseResults: SheetParseResult[] | null = null;
  readonly isLoading = signal(false);
  readonly isImporting = signal(false);
  readonly importResults = signal<ImportResult[] | null>(null);

  get totalValidRows(): number {
    return this.parseResults?.reduce((sum, r) => sum + r.validRowCount, 0) ?? 0;
  }

  get totalImported(): number {
    return this.importResults()?.reduce((sum, r) => sum + r.created, 0) ?? 0;
  }

  get canImport(): boolean {
    const results = this.parseResults;
    if (!this.matchResult || !results) return false;
    if (this.isLoading() || this.isImporting()) return false;
    if (this.importResults() !== null) return false;
    return this.matchResult.matched.some((_, i) => (results[i]?.validRowCount ?? 0) > 0);
  }

  ngOnInit(): void {
    // the timeseries table is loaded on project load
    // but following the pattern of defensively loading it here so we can access it in TimeseriesSheetImporterService
    const plantId = this.projectService.currentProject()?.plantMeta?.id;
    if (plantId) {
      this.timeseriesTablesService.loadTables(plantId).catch((err) => this.errorService.handle(err));
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.errorService.handle(new Error('File Selection Failed'), { userMessage: this.i18n.t('file_selection_failed') });
      return;
    }

    this.selectedFile = file;
    this.matchResult = null;
    this.parseResults = null;
    this.isLoading.set(true);

    try {
      // in future if we have multiple importers, we can extend this importers array
      const importers = [this.tsSheetImporter];
      // get all matches from sheet to importer pages, check all sheets in workbook and all importers to match sheet to a section in the app
      this.matchResult = await this.multiTabImportService.getMatchedSheets(file, importers);
      this.parseResults = await this.multiTabImportService.parseAll(file, this.matchResult.matched);
    } catch (err) {
      this.errorService.handle(err);
    } finally {
      this.isLoading.set(false);
    }
  }

  async onImport(): Promise<void> {
    if (!this.matchResult || !this.parseResults) return;

    this.isImporting.set(true);
    try {
      const results: ImportResult[] = await this.multiTabImportService.importAll(
        this.matchResult.matched,
        this.parseResults,
        { duplicateHandling: this.duplicateHandling },
      );
      // importAll() never throws, per-sheet and per-batch errors are captured in each
      // result. HTTP errors are already surfaced by the api-error interceptor.
      // Keep the dialog open so the user can see the per-sheet results before dismissing.
      this.importResults.set(results);
    } catch (err) {
      this.errorService.handle(err);
    } finally {
      this.isImporting.set(false);
    }
  }

  onDone(): void {
    this.dialogRef.close(this.importResults());
  }
}
